/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `videoTutorialSubmit` for the Caintra Connect frontend.
 * @module utils/videoTutorialSubmit
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger } from "./logger.js";
import {
  createVideoTutorial,
  updateVideoTutorial,
} from "../logic/videoTutorial/index.js";

/**
 * Builds the API payload for create/update video tutorial requests.
 * @param {Object} videoData
 * @param {string} sectionId
 * @param {boolean} includeFileName
 * @returns {Object}
 */
export const buildVideoSubmitData = (
  videoData,
  sectionId,
  includeFileName = false,
) => {
  const submitData = {
    name: videoData.name,
    description: videoData.description,
    sectionId,
    thumbnailSecond: videoData.thumbnailSecond || 0,
  };

  if (includeFileName && videoData.videoFile) {
    submitData.videoTutorialFileName = videoData.videoFile.name;
  }

  return submitData;
};

/**
 * Uploads a video file to a presigned S3 URL.
 * @param {string} presignedUrl
 * @param {File} videoFile
 * @returns {Promise<{ ok: boolean }>}
 */
export const uploadVideoFile = async (presignedUrl, videoFile) => {
  const uploadResponse = await fetch(presignedUrl, {
    method: "PUT",
    body: videoFile,
    headers: {
      "Content-Type": videoFile.type || "application/octet-stream",
    },
  });

  if (!uploadResponse.ok) {
    throw new Error(`Failed to upload video file: ${uploadResponse.statusText}`);
  }

  return { ok: true };
};

/**
 * Updates an existing tutorial, optionally uploading a replacement file.
 * @returns {Promise<{ ok: boolean, result?: Object }>}
 */
export const updateExistingVideoTutorial = async ({
  videoId,
  videoData,
  sectionId,
  navigate,
  withNewFile,
}) => {
  const submitData = buildVideoSubmitData(
    videoData,
    sectionId,
    withNewFile,
  );
  const result = await updateVideoTutorial(videoId, submitData, navigate);

  if (!result) {
    logger.error("Failed to update video tutorial");
    return { ok: false };
  }

  if (withNewFile && result.presignedUrl && videoData.videoFile) {
    await uploadVideoFile(result.presignedUrl, videoData.videoFile);
  }

  return { ok: true, result };
};

/**
 * Creates a new tutorial and uploads its video file when required.
 * @returns {Promise<{ ok: boolean, result?: Object }>}
 */
export const createNewVideoTutorial = async ({
  videoData,
  sectionId,
  navigate,
}) => {
  if (!videoData.videoFile) {
    return { ok: false, missingFile: true };
  }

  const submitData = buildVideoSubmitData(videoData, sectionId, true);
  const result = await createVideoTutorial(submitData, navigate);

  if (!result) {
    logger.error("Failed to create video tutorial");
    return { ok: false };
  }

  if (result.presignedUrl && videoData.videoFile) {
    await uploadVideoFile(result.presignedUrl, videoData.videoFile);
  }

  return { ok: true, result };
};
